/**
 * Bench — panchina (5 slot orizzontali) con drag-target.
 */

import { PlayerAvatar } from "../common/PlayerAvatar";
import { useState } from "react";
import { badgeProbabilita, testoInfoProbabilita } from "../common/probabilita";
import { ruoloMantraClass } from "../common/ruoliMantra";
import type { SlotState } from "./Pitch";
import { siglaRuolo } from "../common/nomenclatura";
import { tr } from "../../i18n";

interface BenchProps {
  slots: SlotState[]; // length variabile (es. 4-5)
  /** Giocatori NON in lista 25 (Andrea 2026-05-21): evidenziati rosso. */
  fuoriLista25Ids?: Set<string>;
  /** Probabilità di giocare (Andrea 2026-09-08): serve anche in
   * panchina — chi entra a gara in corso conta, e un panchinaro OUT
   * va sostituito prima della consegna. */
  probabilitaPerGiocatore?: Map<
    string,
    { probabilita: number; fonte: string; note: string | null }
  >;
  onDropToSlot: (slotIdx: number, giocatoreId: string, sorgente: string) => void;
  onClickPlayer: (slotIdx: number) => void;
}

export function Bench({
  slots,
  fuoriLista25Ids,
  probabilitaPerGiocatore,
  onDropToSlot,
  onClickPlayer,
}: BenchProps) {
  // Andrea 2026-10-23: un tocco/click sulla pastiglia apre il fumetto col
  // motivo (il `title` non si vede sul telefono).
  const [infoId, setInfoId] = useState<string | null>(null);
  return (
    <div className="card p-3 space-y-2">
      <div className="text-xs font-bold text-fg-muted uppercase tracking-wider">
        Panchina ({slots.filter((s) => s.giocatore_id).length}/{slots.length})
      </div>
      {/* Andrea 2026-09-07: righe compatte (gap-1, py-1) — la colonna
          destra ospita anche fasce e rosa disponibile. */}
      <div className="flex flex-col gap-1">
        {slots.map((slot, idx) => {
          const occupied = !!slot?.giocatore_id;
          const isFuoriLista25 =
            occupied &&
            slot?.giocatore_id != null &&
            fuoriLista25Ids?.has(slot.giocatore_id) === true;
          return (
            <div
              key={idx}
              title={
                isFuoriLista25
                  ? tr("⚠ {{v1}} NON è in lista 25 ufficiale", { v1: slot?.nome ?? "?" })
                  : occupied
                    ? (slot?.nome ?? "")
                    : tr("Panchina vuota")
              }
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add("ring-2", "ring-accent");
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove("ring-2", "ring-accent");
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("ring-2", "ring-accent");
                const data = e.dataTransfer.getData("application/x-fantasquad");
                if (!data) return;
                const { giocatore_id, sorgente } = JSON.parse(data);
                onDropToSlot(idx, giocatore_id, sorgente);
              }}
              onClick={() => onClickPlayer(idx)}
              draggable={occupied}
              onDragStart={(e) => {
                if (!occupied || !slot?.giocatore_id) return;
                e.dataTransfer.setData(
                  "application/x-fantasquad",
                  JSON.stringify({
                    giocatore_id: slot.giocatore_id,
                    sorgente: `bench:${idx}`,
                  }),
                );
                e.dataTransfer.effectAllowed = "move";
              }}
              className={`relative w-full rounded-lg border-2 flex items-center gap-2 px-2 py-1 cursor-pointer transition ${
                occupied
                  ? isFuoriLista25
                    ? "bg-danger/30 border-danger ring-2 ring-danger/60 animate-pulse"
                    : "bg-bg-card border-info hover:border-accent"
                  : "bg-primary-deep/30 border-fg-muted/30 border-dashed"
              }`}
            >
              <span className="text-[10px] font-mono text-fg-muted w-6 shrink-0">P{idx + 1}</span>
              {/* Andrea 2026-05-21: avatar foto giocatore se disponibile */}
              {occupied && (
                <PlayerAvatar nome={slot?.nome ?? null} size={28} className="shrink-0" />
              )}
              {/* Andrea 2026-05-23: doppio colore se doppio ruolo —
                  un badge per ogni ruolo mantra (stile Catalogo). */}
              {occupied && slot?.ruoli_mantra && slot.ruoli_mantra.length > 0 && (
                <div className="flex gap-0.5 shrink-0">
                  {slot.ruoli_mantra.map((rm) => (
                    <span
                      key={rm}
                      className={`text-[10px] font-bold min-w-[24px] h-6 px-1 rounded flex items-center justify-center font-mono ${ruoloMantraClass(
                        rm,
                      )}`}
                    >
                      {siglaRuolo(rm)}
                    </span>
                  ))}
                </div>
              )}
              {occupied ? (
                <>
                  <span className="flex-1 min-w-0 text-sm font-bold text-fg truncate">
                    {slot?.nome}
                  </span>
                  {(() => {
                    if (!slot?.giocatore_id || !probabilitaPerGiocatore) return null;
                    const pr = probabilitaPerGiocatore.get(slot.giocatore_id);
                    if (!pr) return null;
                    const b = badgeProbabilita(pr.probabilita, pr.note);
                    const info = testoInfoProbabilita(pr.probabilita, pr.note, pr.fonte);
                    const aperto = infoId === slot.giocatore_id;
                    return (
                      <span className="relative shrink-0">
                        <span
                          className={`text-[9px] font-bold font-mono px-1 py-0.5 rounded ${b.cls}`}
                          title={info}
                          onClick={
                            b.icona
                              ? (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setInfoId((cur) =>
                                    cur === slot.giocatore_id ? null : slot.giocatore_id,
                                  );
                                }
                              : undefined
                          }
                        >
                          {b.label}
                        </span>
                        {aperto && (
                          <span
                            className="absolute bottom-full right-0 mb-1 z-40 w-48 rounded-lg bg-primary-deep border border-accent/50 shadow-xl px-2 py-1.5 text-[10px] leading-snug text-fg whitespace-normal"
                            onClick={(e) => {
                              e.stopPropagation();
                              setInfoId(null);
                            }}
                          >
                            {info}
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </>
              ) : (
                <span className="flex-1 text-xs text-fg-muted italic">{tr("— vuoto —")}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
